import { type FrameBuilderProfile } from './state'

export type OptionalNumber = number | null | undefined

export type FrameSize = [OptionalNumber, OptionalNumber]

/**
 * Determines if number is missing.
 */
const isNumberMissing = (number: OptionalNumber) =>
  typeof number === 'undefined' || number === null

/**
 * Determines if given size larger or equal to minimum size.
 */
const isLargerThan = (size: FrameSize, minimumSize: FrameSize) => {
  if (isNumberMissing(size[0]) || isNumberMissing(size[1])) {
    return true
  }

  const sizeX = size[0] as number
  const sizeY = size[1] as number

  // Compare 1 dimension
  if (!isNumberMissing(minimumSize[0]) && isNumberMissing(minimumSize[1])) {
    const minimumSizeX = minimumSize[0] as number

    return sizeX >= minimumSizeX || sizeY >= minimumSizeX
  }

  if (isNumberMissing(minimumSize[0]) && !isNumberMissing(minimumSize[1])) {
    const minimumSizeY = minimumSize[1] as number

    return sizeX >= minimumSizeY || sizeY >= minimumSizeY
  }

  // Compare 2 dimensions
  if (!isNumberMissing(minimumSize[0]) && !isNumberMissing(minimumSize[1])) {
    const minimumSizeX = minimumSize[0] as number
    const minimumSizeY = minimumSize[1] as number

    return (
      (sizeX >= minimumSizeX && sizeY >= minimumSizeY) ||
      (sizeY >= minimumSizeX && sizeX >= minimumSizeY)
    )
  }

  return true
}

/**
 * Determines if given size smaller or equal to maximum size.
 */
const isSmallerThan = (size: FrameSize, maximumSize: FrameSize) => {
  if (isNumberMissing(size[0]) || isNumberMissing(size[1])) {
    return true
  }

  const sizeX = size[0] as number
  const sizeY = size[1] as number

  // Compare 1 dimension
  if (!isNumberMissing(maximumSize[0]) && isNumberMissing(maximumSize[1])) {
    const maximumSizeX = maximumSize[0] as number

    return sizeX <= maximumSizeX || sizeY <= maximumSizeX
  }

  if (isNumberMissing(maximumSize[0]) && !isNumberMissing(maximumSize[1])) {
    const maximumSizeY = maximumSize[1] as number

    return sizeX <= maximumSizeY || sizeY <= maximumSizeY
  }

  // Compare 2 dimensions
  if (!isNumberMissing(maximumSize[0]) && !isNumberMissing(maximumSize[1])) {
    const maximumSizeX = maximumSize[0] as number
    const maximumSizeY = maximumSize[1] as number

    return (
      (sizeX <= maximumSizeX && sizeY <= maximumSizeY) ||
      (sizeY <= maximumSizeX && sizeX <= maximumSizeY)
    )
  }

  return true
}

/**
 * Determines if given size fits within given minimum and maximum.
 */
export const isSizeWithinBounds = (
  size: FrameSize,
  minimumSize: FrameSize,
  maximumSize: FrameSize,
) => isLargerThan(size, minimumSize) && isSmallerThan(size, maximumSize)

/**
 * Gets an array with frame's total width and height.
 */
export const getFrameSize = (
  artworkWidth?: number,
  artworkHeight?: number,
  passepartoutSize?: number,
): FrameSize => {
  if (!artworkWidth || !artworkHeight) {
    return [undefined, undefined]
  }

  return [
    artworkWidth + (passepartoutSize ?? 0) * 2,
    artworkHeight + (passepartoutSize ?? 0) * 2,
  ]
}

/**
 * Converts an array of numbers into frame size.
 */
export const arrayToSize = (array?: OptionalNumber[]): FrameSize => {
  return [array?.[0], array?.[1]]
}

/**
 * Gets smallest minimum size from all profiles.
 */
export const getMinimumProfileSize = (profiles: FrameBuilderProfile[]) => {
  if (profiles.length === 0) {
    return 1
  }

  const minimumSizes = profiles
    .map((profile) => profile.minimumSize)
    .filter(Boolean) as number[]

  if (minimumSizes.length === 0) {
    return 1
  }

  return Math.min(...minimumSizes)
}

/**
 * Gets largest maximum width from all profiles.
 */
export const getMaximumProfileWidth = (profiles: FrameBuilderProfile[]) => {
  if (!profiles) {
    return 1000
  }

  const maximumWidths = profiles
    .map((profile) => profile.maximumWidth)
    .filter(Boolean) as number[]

  if (maximumWidths.length === 0) {
    return 1000
  }

  return Math.max(...maximumWidths)
}

/**
 * Gets largest maximum height from all profiles.
 */
export const getMaximumProfileHeight = (profiles: FrameBuilderProfile[]) => {
  if (!profiles) {
    return 1000
  }

  const maximumHeights = profiles
    .map((profile) => profile.maximumHeight)
    .filter(Boolean) as number[]

  if (maximumHeights.length === 0) {
    return 1000
  }

  return Math.max(...maximumHeights)
}

/**
 * Determines a size is within given limits.
 */
export const getIsSizeWithinLimits = (
  width: number,
  height: number,
  maximumWidth?: number,
  maximumHeight?: number,
) => {
  if (!maximumWidth || !maximumHeight) {
    return true
  }

  return (
    (width <= maximumWidth && height <= maximumHeight) ||
    (width <= maximumHeight && height <= maximumWidth)
  )
}
